// Hand-defined tables: design tables no dataset file supplies (a lookup, a sample register to
// fill later). A curator adds one with its owner (a member of the project; by default whoever
// adds it) and columns (the same dictionary
// fields as a file column: type, description, primary key, and the same value rules — required,
// unique, minimum/maximum, lengths, pattern, allowed values — through the shared
// dataset/columns/ColumnRules), edits it, links it to and from
// file tables, and deletes it while it is not part of the hosted database. A build creates it
// empty. Rendered by TablesPanel; every change answers with the workspace, a refused column
// ("Column <name>: ...", 422) is shown at that column. Backend: unio/routers/dbdesign_tables.py.
import { useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { Badge, Btn, Field, Input, Select } from "../../components/Shared";
import { Empty } from "../../components/Feedback";
import ColumnRules from "../dataset/columns/ColumnRules";
import { rulesPatch, rulesState } from "../dataset/columns/terms";

const TYPES = ["string", "integer", "number", "boolean", "date", "datetime", "time", "duration",
               "object", "array", "geojson", "geopoint", "any"];
const mono = { fontFamily: "'JetBrains Mono', monospace", fontSize: 13 };
const small = { padding: "6px 12px", fontSize: 13 };
const box = { width: 18, height: 18, margin: 0, accentColor: "var(--dp-accent-dark)" };

const blankColumn = () => ({ name: "", type: "string", primary_key: false, rules: rulesState({}),
                             description: "" });

// owner_id "" = not sent: the server makes the creator the owner (an admin who is no member).
function asForm(t, defaultOwner) {
  if (!t) return { name: "", description: "", owner_id: defaultOwner, columns: [blankColumn()] };
  return {
    id: t.id, name: t.name, description: t.description || "", owner_id: t.owner_id != null ? String(t.owner_id) : "", owner: t.owner,
    columns: t.columns.map(c => ({
      id: c.id, name: c.name, type: c.type, primary_key: c.primary_key, rules: rulesState(c.constraints),
      description: c.description || "", title: c.title,
      sensitivity: c.sensitivity || null,   // no field here; PUT replaces it, so it is sent back as it was
    })),
  };
}

function asBody(f) {
  return {
    name: f.name.trim(), description: f.description.trim() || null,
    ...(f.owner_id ? { owner_id: Number(f.owner_id) } : {}),
    columns: f.columns.filter(c => c.name.trim()).map(c => ({
      ...(c.id ? { id: c.id } : {}), name: c.name.trim(), type: c.type, primary_key: c.primary_key,
      constraints: rulesPatch(c.rules, c.type),
      title: c.title || null, description: c.description.trim() || null,
      sensitivity: c.sensitivity || null,
    })),
  };
}

function TableForm({ initial, owners, defaultOwner, busy, onSubmit, onCancel }) {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const setCol = (i, k, v) => setF(x => ({
    ...x,
    columns: x.columns.map((c, j) => {
      if (k === "primary_key" && v) return j === i ? { ...c, primary_key: true } : { ...c, primary_key: false };
      return j === i ? { ...c, [k]: v } : c;
    }),
  }));
  const setRule = i => key => value => setF(x => ({
    ...x, columns: x.columns.map((c, j) => (j === i ? { ...c, rules: { ...c.rules, [key]: value } } : c)),
  }));
  // The backend names a refused column as "Column <name>: <why>" (services/hand_tables.py).
  const [colError, setColError] = useState(null);
  const title = f.id ? `Edit ${initial.name}` : "New hand-defined table";
  const submit = async e => {
    e.preventDefault();
    setColError(null);
    const ok = await onSubmit(asBody(f), err => {
      const m = /^Column ([a-z0-9_]+): ([\s\S]*)$/.exec(err?.message || "");
      if (m) setColError({ name: m[1], message: m[2] });
    });
    if (ok && !f.id) setF(asForm(null, defaultOwner));
  };
  return (
    <form onSubmit={submit} aria-label={title}
          style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 12, border: "1px solid #e5e7eb",
                   borderRadius: 12, padding: "14px 16px" }}>
      <strong style={{ fontSize: 14 }}>{title}</strong>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
        <Field label="Table name" required hint="lowercase letters, digits, _"><Input value={f.name} onChange={e => set("name", e.target.value)} /></Field>
        <Field label="Owner" hint="a member; once hosted, an editor writes the tables they own">
          <Select options={owners(initial)} value={f.owner_id} onChange={e => set("owner_id", e.target.value)} />
        </Field>
        <Field label="Description"><Input value={f.description} onChange={e => set("description", e.target.value)} /></Field>
      </div>
      <fieldset style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px" }}>
        <legend style={{ fontSize: 12, fontWeight: 600, color: "#374151", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Columns ({f.columns.length}) — without a primary key the table gets a surrogate id
        </legend>
        {f.columns.map((c, i) => (
          <div key={c.id || `new-${i}`} role="group" aria-label={`Column ${i + 1}`}
               style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8,
                        alignItems: "end", padding: "8px 0", borderTop: i ? "1px solid #f3f4f6" : "none" }}>
            <Field label="Name"><Input value={c.name} onChange={e => setCol(i, "name", e.target.value)} style={{ ...mono, padding: "6px 10px" }} /></Field>
            <Field label="Type"><Select options={TYPES} value={c.type} onChange={e => setCol(i, "type", e.target.value)} /></Field>
            <Field label="Description" hint="With its unit, e.g. Volume (mL)"><Input value={c.description} onChange={e => setCol(i, "description", e.target.value)} style={{ padding: "6px 10px" }} /></Field>
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
              <label style={{ display: "flex", gap: 6, fontSize: 13, alignItems: "center" }}>
                <input type="checkbox" style={box} checked={c.primary_key} onChange={e => setCol(i, "primary_key", e.target.checked)} />
                Primary key
              </label>
              <Btn variant="secondary" style={small} disabled={busy}
                   onClick={() => setF(x => ({ ...x, columns: x.columns.filter((_, j) => j !== i) }))}>
                Remove column {i + 1}
              </Btn>
            </div>
            <div style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 10 }}>
              <ColumnRules type={c.type} rules={c.rules} setRule={setRule(i)}
                           columnName={c.name.trim() || `column ${i + 1}`} />
            </div>
            {colError && c.name.trim() && colError.name === c.name.trim() && (
              <p style={{ gridColumn: "1 / -1", margin: 0, fontSize: 13, color: "#b91c1c" }}>{colError.message}</p>
            )}
          </div>
        ))}
        <Btn variant="ghost" style={small} onClick={() => setF(x => ({ ...x, columns: [...x.columns, blankColumn()] }))}>
          Add column
        </Btn>
      </fieldset>
      <div style={{ display: "flex", gap: 10 }}>
        <Btn type="submit" disabled={busy || !f.name.trim()}>{f.id ? "Save table" : "Add table"}</Btn>
        {onCancel && <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>}
      </div>
    </form>
  );
}

function LinkForm({ hand, fileTables, busy, onAdd }) {
  const [subject, setSubject] = useState("");
  const [object, setObject] = useState("");
  const ends = [
    ...hand.flatMap(t => t.columns.map(c => ({ value: `h:${c.id}`, label: `${t.name}.${c.name}`, key: c.primary_key }))),
    ...fileTables.filter(t => t.include).flatMap(t => t.fields.map(f => ({
      value: `f:${f.column_id}`, label: `${t.name}.${f.name}`, key: t.primary_key === f.name }))),
  ];
  const keys = ends.filter(e => e.key);
  const ready = subject && object && (subject.startsWith("h:") || object.startsWith("h:"));
  const asEnd = v => (v.startsWith("h:") ? { hand_column_id: Number(v.slice(2)) } : { column_id: Number(v.slice(2)) });
  const submit = async e => {
    e.preventDefault();
    if (await onAdd({ subject: asEnd(subject), object: asEnd(object) })) { setSubject(""); setObject(""); }
  };
  return (
    <form onSubmit={submit} aria-label="Link a hand-defined table"
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, alignItems: "end", marginTop: 12 }}>
      <Field label="Column" hint="the referencing column"><Select options={ends} value={subject} onChange={e => setSubject(e.target.value)} /></Field>
      <Field label="References key" hint="one end must be a hand-defined table"><Select options={keys} value={object} onChange={e => setObject(e.target.value)} /></Field>
      <div><Btn type="submit" disabled={!ready || busy}>Add link</Btn></div>
    </form>
  );
}

export default function HandTablesPanel({ ws, canManage, busy, onHand }) {
  const hand = ws.hand_tables || [];
  const links = ws.hand_links || [];
  const members = ws.members || [];
  const { user } = useAuth();
  const isMember = !!user && members.some(m => m.id === user.id);
  // A new table is owned by whoever adds it unless another member is picked.
  const defaultOwner = isMember ? String(user.id) : "";
  const owners = t => {
    const opts = members.map(m => ({ value: String(m.id), label: m.role === "curator" ? `${m.username} (curator)` : m.username }));
    if (!isMember && !t?.id) opts.unshift({ value: "", label: `me (${user?.username || "the creator"})` });
    // t is the form state (owner_id a string): an owner who has left the project stays selectable.
    if (t?.id && t.owner_id && !members.some(m => String(m.id) === t.owner_id)) {
      opts.unshift({ value: t.owner_id, label: `${t.owner || "an account"} (no longer a member)` });
    }
    return opts;
  };
  const [editing, setEditing] = useState(null);
  const create = (body, onError) => onHand("post", "/tables", body, `Hand-defined table ${body.name} added.`, onError);
  const save = async (body, onError) => {
    const ok = await onHand("put", `/tables/${editing.id}`, body, `Table ${body.name} saved.`, onError);
    if (ok) setEditing(null);
    return ok;
  };
  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ fontSize: 15, margin: "0 0 8px" }}>Hand-defined tables</h3>
      {hand.length === 0 ? (
        <Empty>No hand-defined tables. Add a table no file supplies — a lookup or a register filled in later; the build creates it empty.</Empty>
      ) : (
        <ul aria-label="Hand-defined tables" style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {hand.map(t => (
            <li key={t.id} style={{ padding: "10px 0", borderTop: "1px solid #e5e7eb" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <strong style={mono}>{t.name}</strong>
                  {t.owner && <Badge color="blue">owner {t.owner}</Badge>}
                  <Badge color="green">key {t.primary_key}{t.surrogate_key ? " (surrogate)" : ""}</Badge>
                  {t.in_hosted_version && <Badge color="gray">in the hosted database</Badge>}
                </div>
                {canManage && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <Btn variant="secondary" style={small} disabled={busy} onClick={() => setEditing(t)}>Edit {t.name}</Btn>
                    <Btn variant="secondary" style={small} disabled={busy || t.in_hosted_version}
                         onClick={() => onHand("del", `/tables/${t.id}`, undefined, `Table ${t.name} deleted.`)}>
                      Delete {t.name}
                    </Btn>
                  </div>
                )}
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                {t.description && <>{t.description} · </>}
                {t.columns.map(c => `${c.name}: ${c.type}`).join(", ") || "no columns"}
              </div>
              {editing?.id === t.id && (
                <TableForm initial={asForm(t, defaultOwner)} owners={owners} defaultOwner={defaultOwner} busy={busy} onSubmit={save} onCancel={() => setEditing(null)} />
              )}
            </li>
          ))}
        </ul>
      )}
      {links.length > 0 && (
        <ul aria-label="Links of hand-defined tables" style={{ listStyle: "none", margin: "12px 0 0", padding: 0 }}>
          {links.map(l => (
            <li key={l.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", padding: "6px 0" }}>
              <span style={mono}>{l.subject.table}.{l.subject.field} → {l.object.table}.{l.object.field}</span>
              {canManage && (
                <Btn variant="secondary" style={small} disabled={busy}
                     onClick={() => onHand("del", `/links/${l.id}`, undefined, "Link removed.")}>
                  Remove link {l.subject.table}.{l.subject.field}
                </Btn>
              )}
            </li>
          ))}
        </ul>
      )}
      {canManage && hand.length > 0 && (
        <LinkForm hand={hand} fileTables={ws.tables} busy={busy}
                  onAdd={body => onHand("post", "/links", body, "Link added.")} />
      )}
      {canManage && !editing && <TableForm initial={asForm(null, defaultOwner)} owners={owners} defaultOwner={defaultOwner} busy={busy} onSubmit={create} />}
    </div>
  );
}
